import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import teams from "./teams";

// Pool scoring values (mirror backend index.js POINTS)
const POINTS = { goalKnockout: 2, r32Win: 10, r16Win: 20, qfWin: 40, sfWin: 80, champion: 150 };

// Assumed scoreline for every hypothetical knockout game
const WIN_GOALS = 2;
const LOSE_GOALS = 1;

// Identify each R32 match by the pair of team apiIds in it (order-independent).
// key = the two apiIds sorted and joined with "|". This anchors the bracket on real
// matchups (which are final and public), NOT on kickoff date order.
const R32_BY_TEAMS = {
  "1531|5529": "M73", "25|2380": "M74", "31|1118": "M75", "6|12": "M76",
  "2|5": "M77", "1090|1501": "M78", "16|2382": "M79", "10|1508": "M80",
  "1113|2384": "M81", "1|13": "M82", "3|27": "M83", "9|775": "M84",
  "15|1532": "M85", "20|32": "M86", "26|1533": "M87", "8|1504": "M88",
};

// Fixed FIFA advancement map: match -> [feeder A, feeder B] (winners of those feed this match)
const FEEDS = {
  M89: ["M74", "M77"], M90: ["M73", "M75"], M91: ["M76", "M78"], M92: ["M79", "M80"],
  M93: ["M83", "M84"], M94: ["M81", "M82"], M95: ["M86", "M88"], M96: ["M85", "M87"],
  M97: ["M89", "M90"], M98: ["M93", "M94"], M99: ["M91", "M92"], M100: ["M95", "M96"],
  M101: ["M97", "M98"], M102: ["M99", "M100"],
  M104: ["M101", "M102"],
};

// Round membership + point value per match
const ROUND_OF = {};
const WINPTS_OF = {};
for (let m = 73; m <= 88; m++) { ROUND_OF["M" + m] = "Round of 32"; WINPTS_OF["M" + m] = POINTS.r32Win; }
["M89","M90","M91","M92","M93","M94","M95","M96"].forEach((m) => { ROUND_OF[m] = "Round of 16"; WINPTS_OF[m] = POINTS.r16Win; });
["M97","M98","M99","M100"].forEach((m) => { ROUND_OF[m] = "Quarter-finals"; WINPTS_OF[m] = POINTS.qfWin; });
["M101","M102"].forEach((m) => { ROUND_OF[m] = "Semi-finals"; WINPTS_OF[m] = POINTS.sfWin; });
ROUND_OF["M104"] = "Final"; WINPTS_OF["M104"] = POINTS.champion;

const ROUND_ORDER = ["Round of 32", "Round of 16", "Quarter-finals", "Semi-finals", "Final"];
const MATCHES_IN_ROUND = {
  "Round of 32": Array.from({ length: 16 }, (_, i) => "M" + (73 + i)),
  "Round of 16": ["M89","M90","M91","M92","M93","M94","M95","M96"],
  "Quarter-finals": ["M97","M98","M99","M100"],
  "Semi-finals": ["M101","M102"],
  "Final": ["M104"],
};

function normalizeRound(r) {
  if (!r) return null;
  if (r.startsWith("Round of 32")) return "Round of 32";
  if (r.startsWith("Round of 16")) return "Round of 16";
  if (r.startsWith("Quarter")) return "Quarter-finals";
  if (r.startsWith("Semi")) return "Semi-finals";
  if (r === "Final") return "Final";
  return null;
}

const apiIdToTeam = {};
teams.forEach((t) => { if (t.apiId != null) apiIdToTeam[String(t.apiId)] = t; });

function Flag({ cc, size = 16 }) {
  if (!cc) return null;
  const h = Math.round(size * 0.75);
  return (
    <img src={"https://flagcdn.com/" + size + "x" + h + "/" + cc.toLowerCase() + ".png"}
      alt="" width={size} height={h}
      style={{ objectFit: "cover", borderRadius: 2, flexShrink: 0, verticalAlign: "middle" }}
      onError={(e) => { e.target.style.display = "none"; }} />
  );
}

export default function WhatIf({ contest, user }) {
  const [entries, setEntries] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState({}); // matchNumber -> apiId (hypothetical)
  const [focusEntryId, setFocusEntryId] = useState("");
  const [bestCaseNote, setBestCaseNote] = useState(null); // { rank, pathCount } after Best Case

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [contest.id]);

  const fetchData = async () => {
    setLoading(true);
    const [entriesSnap, fixturesSnap] = await Promise.all([
      getDocs(query(collection(db, "entries"), where("contestId", "==", contest.id))),
      getDocs(collection(db, "fixtures")),
    ]);
    const loadedEntries = entriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setEntries(loadedEntries);
    setFixtures(fixturesSnap.docs.map((d) => d.data()));
    const mine = loadedEntries.filter((e) => e.userId === user.uid);
    setFocusEntryId((mine[0] || loadedEntries[0] || {}).id || "");
    setLoading(false);
  };

  // Anchor real fixtures to FIFA match numbers by team identity (for R32) and by
  // canonical bracket structure (for later rounds). Store known teams + results per match.
  const state = useMemo(() => {
    const koFixtures = fixtures.filter((f) => normalizeRound(f.round));

    const matchHome = {};   // M -> apiId (known home/first team)
    const matchAway = {};   // M -> apiId
    const matchResult = {}; // M -> winner apiId (if decided)

    const resultFromFixture = (f, home, away) => {
      if (f.status !== "FT") return null;
      if (f.homeScore != null && f.awayScore != null) {
        if (f.homeScore > f.awayScore) return home;
        if (f.awayScore > f.homeScore) return away;
      }
      if (f.homeWinner === true) return home;
      if (f.awayWinner === true) return away;
      return null;
    };

    koFixtures.forEach((f) => {
      const rk = normalizeRound(f.round);
      const home = f.homeTeamId != null ? String(f.homeTeamId) : null;
      const away = f.awayTeamId != null ? String(f.awayTeamId) : null;

      if (rk === "Round of 32") {
        // Identify M-number by the team pair (order-independent)
        if (home && away) {
          const key = [home, away].map(Number).sort((a, b) => a - b).join("|");
          const m = R32_BY_TEAMS[key];
          if (m) {
            matchHome[m] = home; matchAway[m] = away;
            const w = resultFromFixture(f, home, away);
            if (w) matchResult[m] = w;
          }
        }
      } else {
        // Later-round real fixture: identify its M-number by matching its teams to the
        // canonical feeder winners. We place it by finding the match in this round whose
        // feeders' known/possible winners include these teams. Simpler + robust: match by
        // the pair of teams if both are known, else skip (structure will fill it).
        // Try to locate the M in this round whose feeders resolve to these two teams.
        const candidates = MATCHES_IN_ROUND[rk] || [];
        let placed = null;
        for (const m of candidates) {
          if (matchHome[m] || matchAway[m]) continue; // already filled
          placed = m; // fallback: first empty slot in round (kept stable by date sort below)
          break;
        }
        // Prefer structural placement: find the M whose feeders' winners match these teams
        for (const m of candidates) {
          const feed = FEEDS[m];
          if (!feed) continue;
          const w0 = matchResult[feed[0]];
          const w1 = matchResult[feed[1]];
          const set = new Set([w0, w1].filter(Boolean));
          if (home && away && set.has(home) && set.has(away)) { placed = m; break; }
          if ((home && set.has(home) && (!away)) || (away && set.has(away) && (!home))) { placed = m; break; }
        }
        if (placed) {
          if (home) matchHome[placed] = home;
          if (away) matchAway[placed] = away;
          const w = resultFromFixture(f, home, away);
          if (w) matchResult[placed] = w;
        }
      }
    });

    return { matchHome, matchAway, matchResult };
  }, [fixtures]);

  // Resolve the two competitors of a match: known real teams first, else feeder winners.
  const resolve = (m, picksState, memo) => {
    if (memo[m]) return memo[m];
    let home = state.matchHome[m] || null;
    let away = state.matchAway[m] || null;
    if ((!home || !away) && FEEDS[m]) {
      const [fa, fb] = FEEDS[m];
      const wa = winnerOf(fa, picksState, memo);
      const wb = winnerOf(fb, picksState, memo);
      if (!home) home = wa;
      if (!away) away = wb;
    }
    memo[m] = { home, away };
    return memo[m];
  };

  const winnerOf = (m, picksState, memo) => {
    if (state.matchResult[m]) return state.matchResult[m];
    if (picksState[m]) return picksState[m];
    return null;
  };

  const projection = useMemo(() => {
    const memo = {};
    const teamPts = {};
    Object.keys(ROUND_OF).forEach((m) => {
      if (state.matchResult[m]) return; // already counted in totalScore
      const { home, away } = resolve(m, picks, memo);
      if (!home || !away) return;
      const chosen = picks[m];
      if (!chosen) return;
      const loser = chosen === home ? away : home;
      teamPts[chosen] = (teamPts[chosen] || 0) + WINPTS_OF[m] + WIN_GOALS * POINTS.goalKnockout;
      if (loser) teamPts[loser] = (teamPts[loser] || 0) + LOSE_GOALS * POINTS.goalKnockout;
    });
    return teamPts;
  }, [picks, state]);

  const projectedLeaderboard = useMemo(() => {
    const rows = entries.map((e) => {
      let add = 0;
      const epicks = e.picks || {};
      for (let tier = 1; tier <= 10; tier++) {
        const localId = epicks["tier" + tier];
        if (!localId) continue;
        const team = teams.find((t) => t.id === localId);
        if (team && team.apiId != null) add += projection[String(team.apiId)] || 0;
      }
      return {
        id: e.id, entryName: e.entryName || "", userName: e.userName || "",
        projected: (e.totalScore || 0) + add, gain: add, isMine: e.id === focusEntryId,
      };
    });
    rows.sort((a, b) => b.projected - a.projected);
    return rows;
  }, [entries, projection, user, focusEntryId]);

  const stickyRows = useMemo(() => {
    const top = projectedLeaderboard.slice(0, 8);
    const mineOutside = projectedLeaderboard
      .map((r, idx) => ({ ...r, rank: idx + 1 }))
      .filter((r) => r.isMine && r.rank > 8);
    return { top, mineOutside };
  }, [projectedLeaderboard]);

  const showBestCase = () => {
    // Best FINISH (rank), not raw points. Your rank depends on everyone else too, so advancing
    // a team many rivals own (e.g. Portugal) helps them as much as you, while advancing a team
    // few rivals own (e.g. Spain) lifts you above more of the field. We therefore optimize the
    // focused entry's RANK directly: try outcomes, compute the full projected leaderboard, and
    // keep the outcome where the entry finishes highest (ties broken by the entry's own score,
    // then by differentiation — advancing teams owned by fewer rivals).
    const orderedMatches = [];
    ROUND_ORDER.forEach((rk) => MATCHES_IN_ROUND[rk].forEach((m) => orderedMatches.push(m)));

    const win = (m, p) => (state.matchResult[m] ? state.matchResult[m] : (p[m] || null));
    const compsOf = (m, p) => {
      let home = state.matchHome[m] || null;
      let away = state.matchAway[m] || null;
      if ((!home || !away) && FEEDS[m]) {
        if (!home) home = win(FEEDS[m][0], p);
        if (!away) away = win(FEEDS[m][1], p);
      }
      return [home, away];
    };

    // Precompute each entry's set of team apiIds, and how many entries own each team.
    const entryTeamSets = entries.map((e) => {
      const set = new Set();
      const p = e.picks || {};
      for (let tier = 1; tier <= 10; tier++) {
        const localId = p["tier" + tier];
        if (!localId) continue;
        const team = teams.find((t) => t.id === localId);
        if (team && team.apiId != null) set.add(String(team.apiId));
      }
      return { id: e.id, base: e.totalScore || 0, teams: set, isFocus: e.id === focusEntryId };
    });
    const ownerCount = {};
    entryTeamSets.forEach((e) => e.teams.forEach((t) => { ownerCount[t] = (ownerCount[t] || 0) + 1; }));

    // Added knockout points for a given team set under a pick set
    const addedFor = (teamSet, p) => {
      let s = 0;
      orderedMatches.forEach((m) => {
        if (state.matchResult[m]) return;
        const [h, a] = compsOf(m, p);
        if (!h || !a) return;
        const w = p[m];
        if (!w) return;
        const l = w === h ? a : h;
        if (teamSet.has(w)) s += WINPTS_OF[m] + WIN_GOALS * POINTS.goalKnockout;
        if (l && teamSet.has(l)) s += LOSE_GOALS * POINTS.goalKnockout;
      });
      return s;
    };

    const focusSet = (entryTeamSets.find((e) => e.isFocus) || { teams: new Set(), base: 0 });
    const rankOfFocus = (p) => {
      const fscore = focusSet.base + addedFor(focusSet.teams, p);
      let above = 0;
      entryTeamSets.forEach((e) => {
        if (e.isFocus) return;
        const sc = e.base + addedFor(e.teams, p);
        if (sc > fscore) above++;
      });
      return { rank: above + 1, fscore };
    };
    // Only branch on matches whose subtree can carry a FOCUS team (fixes your own path).
    // For all other matches, greedily suppress rivals: advance the team owned by fewer entries.
    const carryCache = {};
    const carriesFocus = (m) => {
      if (carryCache[m] !== undefined) return carryCache[m];
      const h = state.matchHome[m], a = state.matchAway[m];
      let r = false;
      if (state.matchResult[m] && focusSet.teams.has(state.matchResult[m])) r = true;
      if (h && focusSet.teams.has(h)) r = true;
      if (a && focusSet.teams.has(a)) r = true;
      if (FEEDS[m] && (carriesFocus(FEEDS[m][0]) || carriesFocus(FEEDS[m][1]))) r = true;
      carryCache[m] = r;
      return r;
    };
    orderedMatches.forEach((m) => carriesFocus(m));

    // Realism score: prefer brackets where stronger teams (lower tier number) advance. Used only
    // to choose among outcomes that TIE for the best rank, so the displayed bracket looks natural.
    const realismScore = (p) => {
      let r = 0;
      orderedMatches.forEach((m) => {
        if (state.matchResult[m]) return;
        const [h, a] = compsOf(m, p);
        if (!h || !a) return;
        const w = p[m];
        if (!w) return;
        const tw = apiIdToTeam[w];
        // higher reward for stronger team winning (tier 1 best). Weight by round so late-round
        // realism matters more.
        if (tw) r += (11 - tw.tier);
      });
      return r;
    };

    // Compare two candidate results. Primary: best rank. Then: highest focus score. Then:
    // most realistic bracket. (Differentiation is already captured by rank, so realism wins ties.)
    const isBetter = (r, best) => {
      if (!best) return true;
      if (r.rank !== best.rank) return r.rank < best.rank;
      if (r.fscore !== best.fscore) return r.fscore > best.fscore;
      return r.realism > best.realism;
    };

    // Count how many DISTINCT outcomes tie for the very best rank (to flag multiple paths).
    let bestRankSeen = Infinity;
    let bestRankPathCount = 0;

    const solve = (idx, p) => {
      if (idx >= orderedMatches.length) {
        const r = rankOfFocus(p);
        const res = { rank: r.rank, fscore: r.fscore, realism: realismScore(p), picks: { ...p } };
        // track path count for the best rank
        if (res.rank < bestRankSeen) { bestRankSeen = res.rank; bestRankPathCount = 1; }
        else if (res.rank === bestRankSeen) { bestRankPathCount++; }
        return res;
      }
      const m = orderedMatches[idx];
      if (state.matchResult[m]) return solve(idx + 1, p);
      const [h, a] = compsOf(m, p);
      if (!h || !a) return solve(idx + 1, p);
      if (!carriesFocus(m)) {
        // no focus team in this subtree — advance the team owned by fewer entries to suppress
        // rivals; if tied on ownership, advance the stronger seed for realism.
        const ch = ownerCount[h] || 0, ca = ownerCount[a] || 0;
        let pick;
        if (ch !== ca) pick = (ch < ca ? h : a);
        else {
          const th = apiIdToTeam[h], ta = apiIdToTeam[a];
          pick = (th && ta) ? (th.tier <= ta.tier ? h : a) : (th ? h : a);
        }
        return solve(idx + 1, { ...p, [m]: pick });
      }
      let best = null;
      for (const w of [h, a]) {
        const r = solve(idx + 1, { ...p, [m]: w });
        if (isBetter(r, best)) best = r;
      }
      return best;
    };

    const result = solve(0, {});
    setPicks(result.picks);
    setBestCaseNote({ rank: result.rank, pathCount: bestRankPathCount });
  };

  const clearPicks = () => { setPicks({}); setBestCaseNote(null); };

  const pickWinner = (m, apiId) => {
    setBestCaseNote(null);
    setPicks((prev) => {
      const next = { ...prev, [m]: apiId };
      const clearDownstream = (src) => {
        Object.entries(FEEDS).forEach(([target, feed]) => {
          if (feed.includes(src) && next[target] !== undefined) {
            // if the downstream pick is no longer a competitor, clear it
            delete next[target];
            clearDownstream(target);
          }
        });
      };
      clearDownstream(m);
      return next;
    });
  };

  const teamChip = (apiId, m, isWinnerPick, decided) => {
    const t = apiId ? apiIdToTeam[apiId] : null;
    const clickable = !decided && !!apiId;
    return (
      <button
        onClick={clickable ? () => pickWinner(m, apiId) : undefined}
        disabled={!clickable}
        style={{
          display: "flex", alignItems: "center", gap: 6, width: "100%",
          padding: "8px 10px", borderRadius: 8,
          border: "1px solid " + (isWinnerPick ? "rgba(255,215,0,0.7)" : "rgba(255,255,255,0.08)"),
          background: isWinnerPick ? "rgba(255,215,0,0.12)" : "rgba(255,255,255,0.03)",
          color: "white", cursor: clickable ? "pointer" : "default", textAlign: "left",
          opacity: t ? 1 : 0.4,
        }}>
        {t ? <Flag cc={t.cc} size={16} /> : <span style={{ width: 16 }} />}
        <span style={{ fontSize: 12, fontWeight: isWinnerPick ? 700 : 500 }}>{t ? t.name : "TBD"}</span>
        {isWinnerPick && <span style={{ marginLeft: "auto", color: "#FFD700", fontSize: 11 }}>✓</span>}
      </button>
    );
  };

  if (loading) {
    return <div style={{ padding: 20, color: "rgba(255,255,255,0.5)" }}>Loading bracket…</div>;
  }

  const hasR32 = Object.keys(state.matchHome).some((m) => ROUND_OF[m] === "Round of 32");
  if (!hasR32) {
    return (
      <div style={{ padding: 20, color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
        The What-If calculator activates once the Round of 32 is set. Check back after the group stage!
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: "#FFD700", letterSpacing: 1, margin: 0 }}>
          WHAT-IF CALCULATOR
        </h2>
      </div>

      {entries.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: 1, display: "block", marginBottom: 5 }}>
            FOCUS ENTRY
          </label>
          <select
            value={focusEntryId}
            onChange={(e) => setFocusEntryId(e.target.value)}
            style={{
              width: "100%", maxWidth: 360, padding: "8px 10px", borderRadius: 8,
              background: "#16283f", color: "white", border: "1px solid rgba(255,215,0,0.3)",
              fontSize: 13, fontWeight: 600,
            }}>
            {(() => {
              const mine = entries.filter((e) => e.userId === user.uid);
              const others = entries.filter((e) => e.userId !== user.uid);
              return (
                <>
                  {mine.length > 0 && (
                    <optgroup label="My entries">
                      {mine.map((e) => <option key={e.id} value={e.id}>{e.entryName}</option>)}
                    </optgroup>
                  )}
                  {others.length > 0 && (
                    <optgroup label="Other entries">
                      {others.map((e) => <option key={e.id} value={e.id}>{e.entryName} ({e.userName})</option>)}
                    </optgroup>
                  )}
                </>
              );
            })()}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <button onClick={showBestCase} className="btn btn-primary btn-sm" style={{ borderRadius: 8 }}>
          ⭐ Best Case for This Entry
        </button>
        <button onClick={clearPicks} className="btn btn-ghost btn-sm">↺ Clear Picks</button>
      </div>

      {bestCaseNote && (() => {
        const ord = (n) => {
          const s = ["th", "st", "nd", "rd"], v = n % 100;
          return n + (s[(v - 20) % 10] || s[v] || s[0]);
        };
        return (
          <div style={{ marginBottom: 12, background: "rgba(0,200,83,0.08)", border: "1px solid rgba(0,200,83,0.35)", borderRadius: 8, padding: "8px 12px" }}>
            <span style={{ fontSize: 12, color: "#69f0ae", fontWeight: 700 }}>
              ⭐ Your best possible finish is {ord(bestCaseNote.rank)} place.
            </span>
            {bestCaseNote.pathCount > 1 && (
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", display: "block", marginTop: 3 }}>
                ⚑ There are multiple bracket paths that reach {ord(bestCaseNote.rank)} — this is one of them. Others (e.g. advancing different teams of yours) tie for the same finish.
              </span>
            )}
          </div>
        );
      })()}

      <div style={{ marginBottom: 12, background: "rgba(255,215,0,0.06)", border: "1px solid rgba(255,215,0,0.2)", borderRadius: 8, padding: "8px 12px" }}>
        <span style={{ fontSize: 11, color: "rgba(255,215,0,0.8)" }}>
          ⚠️ Every hypothetical game is assumed to finish <strong>2–1</strong>: the winner gets the round bonus plus 2 knockout goals, and the loser still earns 1 knockout goal. Real results replace assumptions as games are played.
        </span>
      </div>

      <div style={{
        position: "sticky", top: 8, zIndex: 20, marginBottom: 16,
        background: "#16283f", border: "1px solid rgba(255,215,0,0.25)", borderRadius: 12,
        padding: "10px 12px", boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: 1 }}>
            PROJECTED LEADERBOARD
          </span>
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>updates as you pick ↓</span>
        </div>
        <div style={{ display: "grid", gap: 3 }}>
          {stickyRows.top.map((row, i) => (
            <div key={row.id} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 5,
              background: row.isMine ? "rgba(255,215,0,0.14)" : "transparent",
              border: row.isMine ? "1px solid rgba(255,215,0,0.35)" : "1px solid transparent",
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", width: 18 }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: row.isMine ? "#FFD700" : "white", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.entryName}
              </span>
              {row.gain > 0 && <span style={{ fontSize: 10, color: "#00C853", fontWeight: 700 }}>+{row.gain}</span>}
              <span style={{ fontSize: 14, fontWeight: 800, color: "#FFD700", minWidth: 34, textAlign: "right" }}>
                {row.projected}
              </span>
            </div>
          ))}
          {stickyRows.mineOutside.map((row) => (
            <div key={row.id} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 5,
              background: "rgba(255,215,0,0.14)", border: "1px solid rgba(255,215,0,0.35)",
              marginTop: 3, borderTop: "1px dashed rgba(255,255,255,0.15)",
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", width: 18 }}>{row.rank}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: "#FFD700", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.entryName}
              </span>
              {row.gain > 0 && <span style={{ fontSize: 10, color: "#00C853", fontWeight: 700 }}>+{row.gain}</span>}
              <span style={{ fontSize: 14, fontWeight: 800, color: "#FFD700", minWidth: 34, textAlign: "right" }}>
                {row.projected}
              </span>
            </div>
          ))}
        </div>
      </div>

      {ROUND_ORDER.map((rk) => {
        const memo = {};
        const nums = MATCHES_IN_ROUND[rk];
        const anyResolvable = nums.some((m) => {
          const { home, away } = resolve(m, picks, memo);
          return home || away;
        });
        if (!anyResolvable) return null;
        // Hide a round once it is fully complete (every match that has real teams is decided).
        // This keeps the tab focused on rounds you can still affect. The Final is kept visible
        // even when decided so the champion is shown.
        if (rk !== "Final") {
          const realMatches = nums.filter((m) => state.matchHome[m] || state.matchAway[m]);
          const allDecided = realMatches.length > 0 && realMatches.every((m) => state.matchResult[m]);
          if (allDecided) return null;
        }
        return (
          <div key={rk} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>
              {rk}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
              {nums.map((m) => {
                const { home, away } = resolve(m, picks, memo);
                if (!home && !away) return null;
                const decided = !!state.matchResult[m];
                const winnerId = decided ? state.matchResult[m] : picks[m];
                return (
                  <div key={m} style={{
                    background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: 10, padding: 8,
                  }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6, minHeight: 12 }}>
                      {decided && <span style={{ fontSize: 9, color: "#00C853", fontWeight: 700 }}>FINAL</span>}
                    </div>
                    <div style={{ display: "grid", gap: 5 }}>
                      {teamChip(home, m, winnerId && winnerId === home, decided)}
                      {teamChip(away, m, winnerId && winnerId === away, decided)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
